// Field validation for the `form.draft` card (SCRUM-278).
//
// The confirm tool in the runtime refuses a CPF that fails the checksum, an
// unreadable date and a missing required value. The card blocks "Aprovar" on
// the same three so the clinic reads the reason under the field instead of a
// refusal after the click.

import type { FormDraftField } from "@/components/generative-ui/envelope/types";

import { brDateToIso, onlyDigits } from "./masks";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const FIELD_MESSAGES = {
  required: "Campo obrigatório.",
  cpf: "CPF inválido. Confira os números.",
  date: "Data inválida. Use dd/mm/aaaa.",
  email: "E-mail inválido.",
  phone: "Telefone inválido. Use DDD e número.",
  time: "Horário inválido. Use hh:mm.",
} as const;

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Brazilian CPF checksum, the same rule the runtime applies. */
export function isValidCpf(value: string): boolean {
  const digits = onlyDigits(value);
  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;

  const checkDigit = (length: number): number => {
    let sum = 0;
    for (let i = 0; i < length; i += 1) {
      sum += Number(digits[i]) * (length + 1 - i);
    }
    const remainder = (sum * 10) % 11;
    return remainder === 10 ? 0 : remainder;
  };

  return checkDigit(9) === Number(digits[9]) && checkDigit(10) === Number(digits[10]);
}

/** The message under one field, or null when the value is acceptable. */
export function fieldError(field: Pick<FormDraftField, "required" | "type">, display: string): string | null {
  const value = display.trim();
  if (value.length === 0) {
    return field.required ? FIELD_MESSAGES.required : null;
  }

  switch (field.type) {
    case "cpf":
      return isValidCpf(value) ? null : FIELD_MESSAGES.cpf;
    case "date":
      return brDateToIso(value) === null ? FIELD_MESSAGES.date : null;
    case "email":
      return EMAIL.test(value) ? null : FIELD_MESSAGES.email;
    case "phone": {
      const digits = onlyDigits(value);
      return digits.length === 10 || digits.length === 11 ? null : FIELD_MESSAGES.phone;
    }
    case "time":
      return TIME.test(value) ? null : FIELD_MESSAGES.time;
    default:
      return null;
  }
}

/** Every message, keyed by field key. An empty object means the form is valid. */
export function fieldErrors(
  fields: Array<Pick<FormDraftField, "key" | "required" | "type">>,
  values: Record<string, string>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of fields) {
    const message = fieldError(field, values[field.key] ?? "");
    if (message) errors[field.key] = message;
  }
  return errors;
}
